import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Download, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  RefreshCw, Plus, List, LayoutGrid, ChevronUp, QrCode, Upload, Edit, Pause, Trash2,
} from "lucide-react";
import { MobileFilterToggle, MobileFilterClose, sidebarClasses } from "@/components/PageWithSidebar";

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  } catch { return dateStr; }
}

export default function TablesPage() {
  const [searchName, setSearchName] = useState("");
  const [areaFilter, setAreaFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilter, setShowFilter] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [detailTab, setDetailTab] = useState("info");
  const [syncing, setSyncing] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);

  const queryParams = useMemo(() => ({
    search: searchName || undefined,
    area: areaFilter || undefined,
    status: statusFilter || undefined,
    page, page_size: pageSize,
  }), [searchName, areaFilter, statusFilter, page, pageSize]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["tables", queryParams],
    queryFn: () => api.get("/tables", { params: queryParams }).then(r => r.data),
    keepPreviousData: true,
  });

  const { data: areasData } = useQuery({
    queryKey: ["table-areas"],
    queryFn: () => api.get("/tables/areas").then(r => r.data.areas || []),
  });

  const tables = data?.tables || [];
  const totalCount = data?.total || 0;
  const areas = areasData || [];
  const totalPages = Math.ceil(totalCount / pageSize);

  const handleSync = async () => {
    setSyncing(true);
    try { const res = await api.post("/tables/sync"); toast.success(res.data.message); refetch(); }
    catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
    finally { setSyncing(false); }
  };

  const handleToggleStatus = async (id) => {
    try { const res = await api.post(`/tables/${id}/toggle-status`); toast.success(res.data.message); refetch(); }
    catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/tables/${id}`); toast.success("Đã xóa phòng/bàn"); refetch(); setDeleteDialog(null); setExpandedRow(null); }
    catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
  };

  const handleExport = () => {
    const headers = ["Tên phòng/bàn","Ghi chú","Khu vực","Số ghế","Trạng thái","Số thứ tự"];
    const rows = tables.map(t => [t.name, t.note, t.area, t.seats, t.status_label, t.order_num]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `phong_ban_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url); toast.success("Đã xuất file CSV");
  };

  const handleDownloadAllQR = () => {
    toast.info("Đang tạo mã QR cho tất cả phòng/bàn...");
    // Simple QR generation notification
    setTimeout(() => toast.success(`Đã tạo ${totalCount} mã QR`), 1000);
  };

  return (
    <div className="flex h-full bg-white dark:bg-slate-900" data-testid="tables-page">
      {/* Left Sidebar */}
      <aside className={sidebarClasses(showFilter)}>
        <MobileFilterClose setShowFilter={setShowFilter} />
        {/* Area filter */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">Khu vực<Plus className="w-4 h-4 text-gray-400 cursor-pointer" /></h4>
          <select value={areaFilter} onChange={e=>{setAreaFilter(e.target.value);setPage(1);}} className="w-full text-sm border border-gray-300 dark:border-slate-600 rounded px-2 py-1.5 bg-white dark:bg-slate-700 dark:text-slate-200" data-testid="tables-area-filter">
            <option value="">--Tất cả--</option>
            {areas.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        {/* Search */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">Tìm kiếm<ChevronUp className="w-4 h-4" /></h4>
          <Input placeholder="Theo tên phòng/bàn" value={searchName} onChange={e=>{setSearchName(e.target.value);setPage(1);}} className="text-sm" data-testid="tables-search" />
        </div>

        {/* Status */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">Trạng thái<ChevronUp className="w-4 h-4" /></h4>
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="status" checked={statusFilter==="active"} onChange={()=>{setStatusFilter("active");setPage(1);}} className="accent-blue-600" />
              <span className="text-sm text-gray-600 dark:text-slate-400">Đang hoạt động</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="status" checked={statusFilter==="inactive"} onChange={()=>{setStatusFilter("inactive");setPage(1);}} className="accent-blue-600" />
              <span className="text-sm text-gray-600 dark:text-slate-400">Ngừng hoạt động</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="status" checked={statusFilter==="all"} onChange={()=>{setStatusFilter("all");setPage(1);}} className="accent-blue-600" />
              <span className="text-sm text-gray-600 dark:text-slate-400">Tất cả</span>
            </label>
          </div>
        </div>

        {/* Page Size */}
        <div className="pt-2 border-t border-gray-200 dark:border-slate-700">
          <label className="text-sm text-gray-600 dark:text-slate-400 flex items-center gap-2">Số bản ghi:
            <select value={pageSize} onChange={e=>{setPageSize(Number(e.target.value));setPage(1);}} className="border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-700 dark:text-slate-200 text-sm" data-testid="tables-page-size">
              <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option><option value={100}>100</option>
            </select>
          </label>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 gap-2 border-b border-gray-200 dark:border-slate-700">
          <h1 className="text-xl font-bold text-gray-800 dark:text-slate-100" data-testid="tables-title">Phòng/Bàn</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={()=>setCreateDialog(true)} className="bg-green-600 hover:bg-green-700 text-white text-sm gap-1" data-testid="btn-create-table"><Plus className="w-4 h-4" /> Thêm phòng/bàn</Button>
            <Button onClick={handleSync} variant="outline" className="text-sm gap-1" disabled={syncing} data-testid="btn-sync-tables"><RefreshCw className={`w-4 h-4 ${syncing?"animate-spin":""}`} />{syncing?"Đồng bộ...":"Đồng bộ"}</Button>
            <Button onClick={handleExport} className="bg-green-600 hover:bg-green-700 text-white text-sm gap-1" data-testid="btn-export-tables"><Download className="w-4 h-4" /> Xuất file</Button>
            <Button onClick={handleDownloadAllQR} variant="outline" className="text-sm gap-1" data-testid="btn-download-qr"><QrCode className="w-4 h-4" /> Tải tất cả mã QR</Button>
            <div className="flex border border-gray-300 dark:border-slate-600 rounded overflow-hidden">
              <button className="p-1.5 bg-blue-600 text-white"><List className="w-4 h-4" /></button>
              <button className="p-1.5 bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300"><LayoutGrid className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm" data-testid="tables-table">
            <thead className="bg-gray-50 dark:bg-slate-800 sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-slate-700">
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Tên phòng/bàn</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Ghi chú</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Khu vực</th>
                <th className="px-3 py-2.5 text-center font-semibold text-gray-700 dark:text-slate-300">Số ghế</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Trạng thái</th>
                <th className="px-3 py-2.5 text-center font-semibold text-gray-700 dark:text-slate-300">Số thứ tự</th>
                <th className="px-3 py-2.5 text-center font-semibold text-gray-700 dark:text-slate-300">Xem mã QR</th>
              </tr>
            </thead>
            <tbody>
              {isLoading?(<tr><td colSpan={7} className="text-center py-10 text-gray-400">Đang tải...</td></tr>):tables.length===0?(<tr><td colSpan={7} className="text-center py-10 text-gray-400">Không có phòng/bàn nào</td></tr>):(
                tables.map(item=>(
                  <React.Fragment key={item.id}>
                    <tr className={`border-b border-gray-100 dark:border-slate-700 cursor-pointer transition-colors ${expandedRow===item.id?"bg-blue-50 dark:bg-blue-900/30 font-medium":"hover:bg-gray-50 dark:hover:bg-slate-800/50"}`} onClick={()=>{setExpandedRow(expandedRow===item.id?null:item.id);setDetailTab("info");}} data-testid={`table-row-${item.name}`}>
                      <td className="px-3 py-2.5 text-gray-800 dark:text-slate-200 font-medium">{item.name}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{item.note}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{item.area}</td>
                      <td className="px-3 py-2.5 text-center text-gray-600 dark:text-slate-400">{item.seats||""}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{item.status_label}</td>
                      <td className="px-3 py-2.5 text-center text-gray-600 dark:text-slate-400">{item.order_num}</td>
                      <td className="px-3 py-2.5 text-center"><span className="text-blue-600 dark:text-blue-400 text-xs cursor-pointer hover:underline" onClick={e=>{e.stopPropagation();toast.info(`Mã QR cho "${item.name}"`);}}>Xem mã QR</span></td>
                    </tr>
                    {expandedRow===item.id&&(
                      <tr className="bg-blue-50/30 dark:bg-blue-900/10"><td colSpan={7} className="p-0">
                        <TableDetail item={item} detailTab={detailTab} setDetailTab={setDetailTab} onToggle={()=>handleToggleStatus(item.id)} onDelete={()=>setDeleteDialog(item)} refetch={refetch} />
                      </td></tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between px-4 sm:px-6 py-2 sm:py-3 gap-2 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="flex items-center gap-1">
            <button onClick={()=>setPage(1)} disabled={page<=1} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronsLeft className="w-4 h-4" /></button>
            <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page<=1} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            {Array.from({length:Math.min(5,totalPages)},(_,i)=>{let pn;if(totalPages<=5)pn=i+1;else if(page<=3)pn=i+1;else if(page>=totalPages-2)pn=totalPages-4+i;else pn=page-2+i;return(<button key={pn} onClick={()=>setPage(pn)} className={`w-8 h-8 rounded text-sm ${page===pn?"bg-blue-600 text-white":"hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-slate-400"}`}>{pn}</button>);})}
            <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page>=totalPages} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={()=>setPage(totalPages)} disabled={page>=totalPages} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronsRight className="w-4 h-4" /></button>
          </div>
          <span className="text-sm text-gray-500 dark:text-slate-400" data-testid="tables-pagination-info">Hiển thị {totalCount>0?(page-1)*pageSize+1:0} - {Math.min(page*pageSize,totalCount)} trên tổng số {totalCount} phòng/bàn</span>
        </div>
      </main>

      <MobileFilterToggle showFilter={showFilter} setShowFilter={setShowFilter} />

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={()=>setDeleteDialog(null)}>
        <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle>Xác nhận xóa phòng/bàn</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600 dark:text-slate-400">Bạn có chắc muốn xóa <strong>{deleteDialog?.name}</strong>?</p>
          <DialogFooter className="gap-2"><Button variant="outline" onClick={()=>setDeleteDialog(null)}>Đóng</Button><Button variant="destructive" onClick={()=>handleDelete(deleteDialog?.id)} data-testid="confirm-delete-table">Xóa</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <CreateTableDialog open={createDialog} areas={areas} onClose={()=>setCreateDialog(false)} onSuccess={()=>{setCreateDialog(false);refetch();}} />
    </div>
  );
}

function TableDetail({ item, detailTab, setDetailTab, onToggle, onDelete, refetch }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const [seats, setSeats] = useState(item.seats);
  const [note, setNote] = useState(item.note);
  const [area, setArea] = useState(item.area);

  const { data: historyData } = useQuery({
    queryKey: ["table-history", item.id],
    queryFn: () => api.get(`/tables/${item.id}/history`).then(r => r.data.history || []),
    enabled: detailTab === "history",
  });

  const handleUpdate = async () => {
    try {
      await api.put(`/tables/${item.id}`, { name, seats, note, area });
      toast.success("Cập nhật thành công");
      setEditing(false);
      refetch();
    } catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
  };

  return (
    <div className="px-6 py-4 border-t border-blue-100 dark:border-blue-800" data-testid="table-detail-panel">
      <div className="flex gap-4 border-b border-gray-200 dark:border-slate-700 mb-4">
        <button onClick={()=>setDetailTab("info")} className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${detailTab==="info"?"border-blue-600 text-blue-600":"border-transparent text-gray-500 dark:text-slate-400"}`} data-testid="tab-table-info">Thông tin</button>
        <button onClick={()=>setDetailTab("history")} className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${detailTab==="history"?"border-blue-600 text-blue-600":"border-transparent text-gray-500 dark:text-slate-400"}`} data-testid="tab-table-history">Lịch sử giao dịch</button>
      </div>

      {detailTab === "info" ? (
        <div>
          <div className="space-y-3 mb-6 max-w-md">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500 dark:text-slate-400 w-28 shrink-0">Tên phòng bàn:</span>
              {editing ? <Input className="text-sm h-8" value={name} onChange={e=>setName(e.target.value)} /> : <span className="text-sm text-gray-800 dark:text-slate-200">{item.name}</span>}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500 dark:text-slate-400 w-28 shrink-0">Số ghế:</span>
              {editing ? <Input type="number" className="text-sm h-8 w-24" value={seats} onChange={e=>setSeats(Number(e.target.value))} /> : <span className="text-sm text-gray-800 dark:text-slate-200">{item.seats||""}</span>}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500 dark:text-slate-400 w-28 shrink-0">Ghi chú:</span>
              {editing ? <Input className="text-sm h-8" value={note} onChange={e=>setNote(e.target.value)} /> : <span className="text-sm text-gray-800 dark:text-slate-200">{item.note||""}</span>}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500 dark:text-slate-400 w-28 shrink-0">Khu vực:</span>
              {editing ? <Input className="text-sm h-8" value={area} onChange={e=>setArea(e.target.value)} /> : <span className="text-sm text-gray-800 dark:text-slate-200">{item.area}</span>}
            </div>
          </div>
          <div className="flex gap-2 pt-3 border-t border-gray-200 dark:border-slate-700">
            {editing ? (
              <>
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs gap-1" onClick={handleUpdate}><Edit className="w-3 h-3" /> Lưu</Button>
                <Button size="sm" variant="outline" className="text-xs" onClick={()=>setEditing(false)}>Hủy</Button>
              </>
            ) : (
              <>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1" onClick={()=>setEditing(true)} data-testid="btn-update-table"><Edit className="w-3 h-3" /> Cập nhật</Button>
                <Button size="sm" className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs gap-1" onClick={onToggle} data-testid="btn-toggle-table"><Pause className="w-3 h-3" /> {item.status==="active"?"Ngừng hoạt động":"Kích hoạt"}</Button>
                <Button size="sm" variant="destructive" className="text-xs gap-1" onClick={onDelete} data-testid="btn-delete-table"><Trash2 className="w-3 h-3" /> Xóa</Button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div>
          {!historyData || historyData.length === 0 ? (
            <p className="text-sm text-gray-400 py-4">Chưa có lịch sử giao dịch</p>
          ) : (
            <div className="border border-gray-200 dark:border-slate-700 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 dark:bg-slate-700"><tr>
                  <th className="px-3 py-2 text-left font-medium">Mã hóa đơn</th>
                  <th className="px-3 py-2 text-left font-medium">Thời gian</th>
                  <th className="px-3 py-2 text-left font-medium">Khách hàng</th>
                  <th className="px-3 py-2 text-right font-medium">Tổng tiền</th>
                </tr></thead>
                <tbody>{historyData.map((h,idx)=>(<tr key={idx} className="border-t border-gray-100 dark:border-slate-700">
                  <td className="px-3 py-2 text-blue-600">{h.code}</td>
                  <td className="px-3 py-2">{formatDateTime(h.date)}</td>
                  <td className="px-3 py-2">{h.customer||"Khách lẻ"}</td>
                  <td className="px-3 py-2 text-right font-medium">{formatVND(h.total)}</td>
                </tr>))}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CreateTableDialog({ open, areas, onClose, onSuccess }) {
  const [name, setName] = useState("");
  const [area, setArea] = useState("");
  const [seats, setSeats] = useState("");
  const [note, setNote] = useState("");
  const [orderNum, setOrderNum] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error("Vui lòng nhập tên phòng/bàn"); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/tables", { name: name.trim(), area, seats: Number(seats)||0, note, order_num: Number(orderNum)||0 });
      toast.success(res.data.message);
      setName(""); setArea(""); setSeats(""); setNote(""); setOrderNum("");
      onSuccess();
    } catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Thêm phòng/bàn mới</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><label className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 block">Tên phòng/bàn *</label><Input placeholder="VD: SÂN 9, P201..." value={name} onChange={e=>setName(e.target.value)} data-testid="create-table-name" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 block">Khu vực</label>
              <select value={area} onChange={e=>setArea(e.target.value)} className="w-full text-sm border border-gray-300 dark:border-slate-600 rounded px-3 py-2 bg-white dark:bg-slate-700 dark:text-slate-200" data-testid="create-table-area">
                <option value="">-- Chọn khu vực --</option>
                {areas.map(a=><option key={a} value={a}>{a}</option>)}
                <option value="__new">+ Thêm khu vực mới</option>
              </select>
            </div>
            <div><label className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 block">Số ghế</label><Input type="number" placeholder="0" value={seats} onChange={e=>setSeats(e.target.value)} data-testid="create-table-seats" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 block">Ghi chú</label><Input placeholder="..." value={note} onChange={e=>setNote(e.target.value)} data-testid="create-table-note" /></div>
            <div><label className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 block">Số thứ tự</label><Input type="number" placeholder="Auto" value={orderNum} onChange={e=>setOrderNum(e.target.value)} data-testid="create-table-order" /></div>
          </div>
        </div>
        <DialogFooter className="gap-2 mt-4"><Button variant="outline" onClick={onClose}>Đóng</Button><Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white" data-testid="submit-create-table">{submitting?"Đang tạo...":"Thêm phòng/bàn"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
